import React, { useContext, useEffect, useState } from "react";
import {
  TableContainer,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Grid,
  Checkbox,
  FormControl,
  FormControlLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
} from "@material-ui/core";
import { useQuery } from "@apollo/client";
import { styled } from "@material-ui/styles";

import { MatchContext } from "../../context";
import { SET_DATA, SET_HIGHLIGHTED_COLS } from "../../reduce/actionTypes";
import useStyles from "./styles";
import { EXPORT_GRAPH } from "../../queries/stockpiles.gql";
import { prepareQueryForExportBookmarks } from "../../queries/bookmarks.gql";
import {
  exportGraph,
  generateDocFile,
  generatePowerpointFile,
} from "../../utils/export-highchart";
import ErrorMessage from "../common/error-message.component";
import { APP_INSIGHTS_EVENTS, EXPORT_TAG_MAXIMUM } from "../../constants";
import useTrackEvent from "../../utils/hooks/use-track-app-event";

const frontSensorsList = [
  "a_2004FI662",
  "a_2004FI672",
  "Total_Zn_Rougher_Airflow",
  "v_2030FIC751_RATIO",
  "v_2005FI002_RAT",
];

type ExportFileType = "docx" | "pptx";

const GraphExportModal = ({ highlightedCols }): JSX.Element => {
  const trackEvent = useTrackEvent();
  const { state, dispatch } = useContext(MatchContext);
  const classes = useStyles();
  const [sensorSelected, setSensorSelected] = useState([]);
  const [exportBtnDisabled, setExportBtnDisabled] = useState(false);
  const [exportFileType, setExportFileType] = useState<ExportFileType>("docx");
  const [highlightedStockpile, setHighlightedStockpile] = useState([]);
  const [stockpileList, setStockpileList] = useState([]);
  const [sensorsList, setSensorsList] = useState([]);
  const [isExportBtnClicked, setIsExportBtnClicked] = useState(false);
  const [base64ImagesError, setBase64ImagesError] = useState(null);
  const [base64ImagesLoading, setBase64ImagesLoading] = useState(false);

  const StyledTableRow = styled(TableRow)(() => ({
    "&:nth-of-type(even)": {
      backgroundColor: "#fafafa",
    },
    "&:hover:nth-of-type(even)": {
      backgroundColor: "#fafafa !important",
    },
    "&:nth-of-type(odd)": {
      backgroundColor: "transparent",
    },
    "&:hover:nth-of-type(odd)": {
      backgroundColor: "transparent !important",
    },
  }));

  const { data: bookmarkData } = useQuery(
    prepareQueryForExportBookmarks(
      highlightedCols.length === 0 ? ["skip"] : highlightedCols,
      sensorSelected.length === 0 ? ["skip"] : sensorSelected
    ),
    {
      fetchPolicy: "no-cache",
      skip:
        (highlightedCols.length ?? 0) === 0 ||
        (sensorSelected.length ?? 0) === 0,
    }
  );

  const combinedBookmarks = Object.values(bookmarkData ?? {})
    .map((bookmark) => bookmark)
    .flat()
    .map((bookmark) => {
      delete (bookmark as any).__typename;
      return bookmark;
    });

  const { refetch: refetchBase64Images } = useQuery(EXPORT_GRAPH, {
    variables: {
      inputGraphOptionData: {
        isBookMarkChecked: state.exportBookmarks,
        isBestDayChecked: state.exportBestDays,
        sensorIds: sensorSelected,
        selectedStockpiles: highlightedStockpile,
        bookmarks: combinedBookmarks,
      },
    },
    fetchPolicy: "no-cache",
    skip:
      !isExportBtnClicked ||
      (highlightedStockpile.length ?? 0) === 0 ||
      (sensorSelected.length ?? 0) === 0,
  });

  useEffect(() => {
    setSensorSelected(
      sensorsList
        .filter(({ value }) => frontSensorsList.includes(value))
        .map(({ value }) => value)
    );
  }, [sensorsList]);

  useEffect(() => {
    setSensorsList([
      ...state.sensorsList?.filter(({ value }) =>
        frontSensorsList.includes(value)
      ),
      ...state.sensorsList?.filter(
        ({ value }) => !frontSensorsList.includes(value)
      ),
    ]);
  }, [state.sensorsList]);

  useEffect(() => {
    const listOfStockpile = [];

    state.stockpiles?.forEach((stockpileObj) => {
      listOfStockpile.push(stockpileObj.stockpile);
    });

    setStockpileList(listOfStockpile);
  }, [state.stockpiles]);

  useEffect(() => {
    if (highlightedCols.length !== 0) {
      setHighlightedStockpile(highlightedCols);
    }
  }, [highlightedCols]);

  useEffect(() => {
    if (
      !highlightedStockpile ||
      sensorSelected.length === 0 ||
      base64ImagesLoading
    ) {
      setExportBtnDisabled(true);
      return;
    }

    setExportBtnDisabled(false);
  }, [highlightedStockpile, sensorSelected, base64ImagesLoading]);

  const handleCloseModal = () => {
    dispatch({
      type: SET_DATA,
      payload: { showGraphExportModal: false },
    });
  };

  const handleClickRow = (event, name, isItemSelected) => {
    const selectedIndex = sensorSelected.indexOf(name);
    let newSelected = [];

    if (sensorSelected.length >= EXPORT_TAG_MAXIMUM && !isItemSelected) return;

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(sensorSelected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(sensorSelected.slice(1));
    } else if (selectedIndex === sensorSelected.length - 1) {
      newSelected = newSelected.concat(sensorSelected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        sensorSelected.slice(0, selectedIndex),
        sensorSelected.slice(selectedIndex + 1)
      );
    }

    setSensorSelected(newSelected);
  };

  const isSensorSelected = (value) => sensorSelected.indexOf(value) !== -1;

  const handleExportFileTypeChange = (event) => {
    setExportFileType(event.target.value);
  };

  const handleGraphExport = () => {
    setBase64ImagesLoading(true);
    setBase64ImagesError(null);
    trackEvent(APP_INSIGHTS_EVENTS.EXPORT_GRAPH, {
      stockpileName: state.stockpileName,
      graphs: sensorSelected,
    });
    refetchBase64Images({
      inputGraphOptionData: {
        isBookMarkChecked: state.exportBookmarks,
        isBestDayChecked: state.exportBestDays,
        sensorIds: sensorSelected,
        selectedStockpiles: highlightedStockpile,
        bookmarks: combinedBookmarks,
      },
    })
      .then((newImage) => {
        let fileToExport;
        if (!newImage.data || newImage.loading) {
          return;
        }
        if (exportFileType === "docx") {
          fileToExport = generateDocFile(newImage.data, sensorsList);
        } else {
          fileToExport = generatePowerpointFile(newImage.data, sensorsList);
        }
        exportGraph(state.stockpileName, exportFileType, fileToExport);
        setIsExportBtnClicked(false);
        setBase64ImagesLoading(false);
        setBase64ImagesError(null);
      })
      .catch((e) => {
        setBase64ImagesError(e);
      });
  };

  const handleStockpileChange = (event) => {
    setHighlightedStockpile([...highlightedStockpile, event.target.value]);

    dispatch({
      type: SET_HIGHLIGHTED_COLS,
      payload: [...highlightedStockpile, event.target.value],
    });
  };

  const handleChangeBookmarks = () => {
    dispatch({
      type: SET_DATA,
      payload: {
        exportBookmarks: !state.exportBookmarks,
      },
    });
  };

  const handleChangeBestDays = () => {
    dispatch({
      type: SET_DATA,
      payload: {
        exportBestDays: !state.exportBestDays,
      },
    });
  };

  return (
    <div className={classes.matchingRequestContainer}>
      <Paper className={classes.main}>
        <Grid container className={classes.headContainer}>
          <Grid
            item
            container
            xs={3}
            justifyContent="flex-start"
            alignContent="center"
            className={classes.exportModalHeader}
          >
            Export Graphs
          </Grid>
        </Grid>
        <Grid
          item
          container
          xs={4}
          justifyContent="flex-start"
          alignContent="center"
          className={classes.listHeader}
        >
          Selected Stockpiles
          <ul className={classes.stockpileListContainer}>
            {highlightedStockpile
              ? highlightedStockpile.map((item) => <li>{item}</li>)
              : null}
          </ul>
          <FormControl className={classes.formControl}>
            <Select value={""} displayEmpty onChange={handleStockpileChange}>
              <MenuItem value="">
                <em>Add Stockpile</em>
              </MenuItem>
              {stockpileList
                ?.filter(
                  (stockpile) => !highlightedStockpile.includes(stockpile)
                )
                .map((key) => {
                  return <MenuItem value={key}>{key}</MenuItem>;
                })}
            </Select>
          </FormControl>
        </Grid>
        <Grid container className={classes.listBody}>
          <Grid
            item
            container
            xs={6}
            justifyContent="flex-start"
            alignContent="center"
            style={{ paddingTop: "1rem", paddingBottom: "1rem" }}
          >
            Selected Graphs ({sensorSelected.length} out of {sensorsList.length}
            )
          </Grid>
          <Grid
            item
            container
            xs={6}
            justifyContent="flex-end"
            alignContent="center"
          ></Grid>
        </Grid>
        <div className={classes.sensorListContainer}>
          {base64ImagesError ? (
            <ErrorMessage error={base64ImagesError} />
          ) : (
            <TableContainer>
              <Table>
                <TableBody className={classes.sensorListTable}>
                  {sensorsList.map((item) => {
                    const isItemSelected = isSensorSelected(item.value);
                    return (
                      <StyledTableRow
                        onClick={(event) =>
                          handleClickRow(event, item.value, isItemSelected)
                        }
                        selected={isItemSelected}
                      >
                        <TableCell className={classes.sensorListTableCell}>
                          {frontSensorsList.includes(item.value) ? (
                            <b>{item.label}</b>
                          ) : (
                            item.label
                          )}
                        </TableCell>
                        <TableCell className={classes.sensorListTableCheckBox}>
                          <Checkbox
                            name={item.value}
                            color="primary"
                            checked={isItemSelected}
                            disabled={
                              sensorSelected.length >= EXPORT_TAG_MAXIMUM &&
                              !isItemSelected
                            }
                          />
                        </TableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
        <Grid container className={classes.listBody}>
          <Grid>
            File Type:
            <FormControl className={classes.formControl}>
              <Select
                value={exportFileType ?? ""}
                onChange={handleExportFileTypeChange}
              >
                <MenuItem value="docx">
                  <em>Word Doc (.docx)</em>
                </MenuItem>
                <MenuItem value="pptx">
                  <em>Powerpoint Doc (.pptx)</em>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={state.exportBookmarks}
                  onChange={handleChangeBookmarks}
                />
              }
              label="Bookmarks"
              name="bookmarks-checked"
              labelPlacement="start"
              className={classes.formControlLeftMargin}
            />
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={state.exportBestDays}
                  onChange={handleChangeBestDays}
                />
              }
              label="Best days"
              name="bestDays-checked"
              labelPlacement="start"
            />
          </Grid>
        </Grid>
        <Grid
          container
          style={{ backgroundColor: "#fafafa" }}
          className={classes.exportModalFooter}
        >
          <Grid item container xs={8}>
            {sensorSelected.length >= EXPORT_TAG_MAXIMUM &&
            !exportBtnDisabled ? (
              <FormHelperText error>
                *Only maximum {EXPORT_TAG_MAXIMUM} graphs supported for each
                export
              </FormHelperText>
            ) : null}
          </Grid>
          <Grid container item xs={2} alignContent="center" justify="flex-end">
            <div style={{ marginLeft: "1rem" }}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleCloseModal}
              >
                Cancel
              </Button>
            </div>
          </Grid>
          <Grid
            container
            item
            xs={2}
            alignContent="center"
            justifyContent="flex-end"
          >
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={exportBtnDisabled}
              onClick={handleGraphExport}
            >
              {base64ImagesLoading ? (
                <CircularProgress color="inherit" size="1.4rem" />
              ) : (
                " Export"
              )}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
};

export default GraphExportModal;
